import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { getAPI } from 'src/api';
import { GetClientDataRoomResponse } from 'src/generated/api';
import { tss } from 'tss-react';
import {
  Theme,
  useTheme,
  Table,
  Sheet,
  Typography,
  Select,
  Option,
  Button,
  IconButton,
} from '@mui/joy';
import { formatDate, formatFileSize } from 'src/utils';
import { ChevronLeft, CloudDownload, Download } from '@mui/icons-material';

const useStyles = tss
  .withParams<{ theme: Theme }>()
  .create(({ theme }) => ({
    container: {
      display: 'flex',
      flexDirection: 'column',
      padding: theme.spacing(2),
      maxWidth: 1200,
      marginLeft: 'auto',
      marginRight: 'auto',
    },
    title: {
      display: 'flex',
      flexDirection: 'row',
    },
    header: {
      display: 'flex',
      flexDirection: 'row',
      alignItems: 'flex-end',
      gap: theme.spacing(2),
    },
    downloadAllButton: {
      display: 'flex',
      alignItems: 'center',
      flexDirection: 'row',
      gap: theme.spacing(1),
    },
    table: {
      marginTop: theme.spacing(2),
      marginBottom: theme.spacing(2),
    },
    tableCell: {
      padding: theme.spacing(1, 2),
      textAlign: 'left',
    },
    nameColumn: {
      flexGrow: 1,
    },
    categoryColumn: {
      width: 150,
    },
    smallCol: {
      width: 128,
    },
    actionsColumn: {
      width: '25%',
    },
    filterContainer: {
      width: 200,
      marginLeft: 'auto',
    },
  }));

export default function DataRoom() {
  const { id } = useParams();
  const [data, setData] = useState<GetClientDataRoomResponse>();
  const [selectedCategory, setSelectedCategory] = useState<string>('all');
  const api = getAPI();
  const fetch = useCallback(async () => {
    const res = await api.clients.getClientDataRoom(id!);
    setData(res.data);
  }, [id, api]);
  useEffect(() => {
    fetch();
  }, [fetch]);
  const theme = useTheme();
  const { classes } = useStyles({ theme });

  const categories = ['all', ...new Set(data?.files.map((file) => file.category) || [])];

  const filteredFiles = data?.files.filter((file) =>
    selectedCategory === 'all' || file.category === selectedCategory,
  );

  const handleDownloadAll = async () => {
    const response = await api.clients.downloadAllDataRoomFiles(id!, {
      format: 'blob',
    });
    const url = window.URL.createObjectURL(new Blob([response.data]));
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', 'data_room.zip');
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  };

  const handleDownloadFile = useCallback(async (category: string, fileName: string) => {
    const response = await api.clients.downloadClientDataRoomFile(id!, category, fileName, {
      format: 'blob',
    });
    const url = window.URL.createObjectURL(new Blob([response.data]));
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', fileName);
    document.body.appendChild(link);
    link.click();
    link.remove();
  }, [api, id]);

  const navigate = useNavigate();

  return (
    <div className={classes.container}>
      <div className={classes.header}>
        <div className={classes.title}>
          <IconButton
            onClick={() => navigate(`/clients/${id}`)}
          >
            <ChevronLeft />
          </IconButton>
          <Typography level="h2">Data Room</Typography>
        </div>
        <div className={classes.filterContainer}>
          <Typography level="body-sm">Category</Typography>
          <Select
            value={selectedCategory}
            onChange={(_, value) => setSelectedCategory(value as string)}
          >
            {categories.map((category) => (
              <Option key={category} value={category}>
                {category.charAt(0).toUpperCase() + category.slice(1)}
              </Option>
            ))}
          </Select>
        </div>
        <Button
          onClick={handleDownloadAll}
          size="md"
          className={classes.downloadAllButton}
        >
          <CloudDownload />
          Download All
        </Button>
      </div>
      <Sheet className={classes.table}>
        <Table>
          <thead>
            <tr>
              <th className={`${classes.tableCell} ${classes.nameColumn}`}>Name</th>
              <th className={`${classes.tableCell} ${classes.categoryColumn}`}>Category</th>
              <th className={`${classes.tableCell} ${classes.smallCol}`}>Size</th>
              <th className={`${classes.tableCell} ${classes.smallCol}`}>Upload Date</th>
              <th className={`${classes.tableCell} ${classes.smallCol}`} />
            </tr>
          </thead>
          <tbody>
            {filteredFiles?.map((file) => (
              <tr key={`${file.category}/${file.name}`}>
                <td className={`${classes.tableCell} ${classes.nameColumn}`}>{file.name}</td>
                <td className={`${classes.tableCell} ${classes.categoryColumn}`}>{file.category}</td>
                <td className={`${classes.tableCell} ${classes.smallCol}`}>{formatFileSize(file.size)}</td>
                <td className={`${classes.tableCell} ${classes.smallCol}`}>{formatDate(file.uploadedAt)}</td>
                <td className={`${classes.tableCell} ${classes.smallCol}`}>
                  <IconButton
                    onClick={() => handleDownloadFile(file.category, file.name)}
                    color="neutral"
                    variant="outlined"
                  >
                    <Download />
                  </IconButton>
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
      </Sheet>
    </div>
  );
}
